import { getReviewSubmissionClientData } from '../helper/get-review-submission-client-data';
import { emitFormErrorActionTracking } from '../tracking/submit-action';
import { showSuccessView } from '../success-view/show-success-view';
import { submitReview } from './submission/submit-review';
import { trackServerAction } from './track-server-action';
import { checkAndHandleValidity } from './validity-check/check-and-handle-validity';
import { FormWidget } from '../form-widget';
import { setSubmitButtonState } from '../helper/set-submit-button-state';
import { push as showSnackbar } from '@otto-ec/otto-components/snackbar/v1';

export type SubmitButtonClickHandlerParams = {
    rootElement: Pick<HTMLElement, 'querySelector' | 'querySelectorAll' | 'dataset'>;
    formWidgets: FormWidget[];
};

export const SubmitButtonClickHandler = ({ rootElement, formWidgets }: SubmitButtonClickHandlerParams) => {
    let isHandlingClick = false;

    return async () => {
        if (isHandlingClick) return;

        const clientData = getReviewSubmissionClientData({ rootElement });
        if (clientData.status !== 'CAN_SUBMIT') throw new Error('Review submission data is not submittable');

        isHandlingClick = true;
        try {
            const check = await checkAndHandleValidity({ rootElement, formWidgets });
            if (!check.valid) {
                emitFormErrorActionTracking({
                    errorReason: check.reason,
                    variationId: clientData.variation.id,
                    source: clientData.reviewSource,
                });
                return;
            }

            const multipartUploadActive = clientData.multipartReviewImageUploadActive === true;

            setSubmitButtonState({ rootElement, disabled: true, loading: true });
            try {
                await submitReview({ rootElement, multipartUploadActive });
            } catch {
                await trackServerAction({ rootElement, success: false });
                showSnackbar(
                    'Das hat leider nicht geklappt. Bitte versuche es noch einmal oder speichere deine Eingaben und versuche es später erneut.'
                );
                setSubmitButtonState({ rootElement, disabled: false, loading: false });
                return;
            }

            const suggestionCount = await showSuccessView({ rootElement, clientData }).catch(() => undefined);
            const furtherReviewReminders = clientData.followUpReviewSuggestions?.toggleActive ? (suggestionCount ?? 0) : undefined;

            await Promise.allSettled([trackServerAction({ rootElement, success: true, furtherReviewReminders })]);
        } finally {
            isHandlingClick = false;
        }
    };
};
